'use client'

import ScrollLoadMore from '@/components/scroll-load-more'
import WalletOffers from '@/components/offers/wallet-offers'
import React, { FC, ReactElement } from 'react'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@/components/ui/select'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import axios from 'axios'
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Skeleton } from '@/components/ui/skeleton'
import copy from 'copy-to-clipboard'
import { getUnfreezableSats } from '@/utils/unfreeze'

import Wallet from '@/utils/wallet'
import useWalletBalance from '@/utils/hooks/useWalletBalance'
import useAcquisitions, { acquiredAtMs } from '@/utils/hooks/useAcquisitions'
import useBrc20Balance from '@/utils/hooks/useBrc20Balance'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import { lowerQuote, useMarks } from '@/lib/quotes/hooks'
import Ticker from '@/components/ticker'
import useTokenPrices from '@/utils/hooks/useTokenPrices'
import useSatoshiPrice from '@/utils/hooks/useSatoshiPrice'
import useSatoshiPriceUSD, { getSatoshiPriceUSD } from '@/utils/hooks/useSatoshiPriceUSD'
import useSwr from 'swr'
import { fetcher } from '@/utils/fetcher'
import {
	API_URL,
	CDN_URL,
	STORAGE_PROFILE,
	NAMES_SLUGS,
	COLLECTIONS_VERSION
} from '@/utils/constants'
import abbreviateAddress from '@/utils/abbreviate-address'
import formatNumber from '@/utils/format-number'
import isRune from '@/utils/is-rune'
import isAlkanes from '@/utils/is-alkanes'
import alkanesPathForSlug from '@/utils/alkanes-route'
import { useRouter } from 'next/navigation'
import useAlkanesPrices from '@/utils/hooks/useAlkanesPrices'
import isFungible from '@/utils/is-fungible'
import Outpoint from '@/utils/outpoint'
import useInscriptionModal from '@/utils/hooks/useInscriptionModal'
import useIsHydrated from '@/utils/hooks/useIsHydrated'
import UserIconStatic from '@/components/user-icon-static'
import CollectionIcon from '@/components/collection-icon'
import UsedTransferBadge from '@/components/used-transfer-badge'
import InscriptionContent from '@/components/inscription-content'
import BitcoinIcon from '@/icons/bitcoin-filled'
import Link from '@/components/link'
import Event from '@/utils/event'
import CollectionDetail from '@/components/search-input/pages/collection-detail'
import ImportListings from '@/components/search-input/pages/import-listings'
import { toast } from '@/components/ui/use-toast'
import useViewerProfile from '@/app/alkanes/lib/use-viewer-profile'
import { resolveTwetchImage } from '@/app/alkanes/lib/twetch-media'
import { openProfileEditor } from '@/app/alkanes/lib/twetch-ui'
import RecoverySweepPanel from '@/components/recovery-sweep-panel'
import TwetchProfileHost from '@/app/alkanes/components/twetch-profile-host'

// Format USD with commas
const formatUSD = (value: number | string): string => {
	const num = typeof value === 'string' ? parseFloat(value) : value
	if (isNaN(num)) return '0.00'
	return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const parseNumericValue = (value: unknown): number | null => {
	if (typeof value === 'number') {
		return Number.isFinite(value) ? value : null
	}

	if (typeof value !== 'string') {
		return null
	}

	const parsed = parseFloat(value.replace(/,/g, ''))
	return Number.isFinite(parsed) ? parsed : null
}

const readStoredProfile = (): { icon?: string; sns?: string } => {
	try {
		if (typeof localStorage === 'undefined') return {}
		const profile = localStorage.getItem(STORAGE_PROFILE)
		return profile ? JSON.parse(profile) : {}
	} catch {
		return {}
	}
}

const getFungibleTicker = (insc: any): string => {
	if (insc?.ticker) {
		return insc.ticker.toString().toUpperCase()
	}

	const slug = insc?.collection?.slug
	if (!slug || typeof slug !== 'string') {
		return 'TOKEN'
	}

	const derived = slug.split('-').slice(1).join('-').replace(/dot/g, '.').toUpperCase()
	return derived || 'TOKEN'
}

const getFungibleAmount = (insc: any): number | null => {
	const directAmount = parseNumericValue(insc?.amount)
	if (directAmount !== null) {
		return directAmount
	}

	const attributes = Array.isArray(insc?.meta?.attributes) ? insc.meta.attributes : []
	const amountAttribute =
		attributes.find((attr: any) => attr?.trait_type?.toLowerCase?.().includes('amount')) ||
		attributes.find((attr: any) => parseNumericValue(attr?.value) !== null)

	return parseNumericValue(amountAttribute?.value)
}

const getFungibleProtocol = (insc: any): string => {
	const slug = insc?.collection?.slug || ''

	if (isRune(slug)) return 'RUNE'
	if (isAlkanes(slug)) return 'ALKANES'
	if (slug.startsWith('tap-')) return 'TAP'
	if (slug.startsWith('brc20-') || slug.startsWith('drc20-') || slug.startsWith('bel20-')) {
		return 'BRC-20'
	}

	return 'TOKEN'
}

const getFungibleDisplayLabel = (insc: any): string => {
	const ticker = getFungibleTicker(insc)
	const amount = getFungibleAmount(insc)

	if (amount === null) {
		return ticker
	}

	return `${formatNumber(amount)} ${ticker}`
}

const isUsedTransfer = (insc: any): boolean => {
	if (!isFungible(insc?.collection?.slug)) {
		return false
	}

	const inscriptionId = insc?.id
	const outpointHex = insc?.outpoint?.outpoint

	if (!inscriptionId || !outpointHex || typeof inscriptionId !== 'string') {
		return false
	}

	const inscriptionTxid = inscriptionId.split('i')[0]
	if (!inscriptionTxid || inscriptionTxid.length !== 64) {
		return false
	}

	try {
		return Outpoint.decode(outpointHex).txid !== inscriptionTxid
	} catch {
		return false
	}
}

// Icons
const CopyIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="14"
		height="14"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
		<path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
	</svg>
)

const CheckIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="14"
		height="14"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<polyline points="20 6 9 17 4 12" />
	</svg>
)

const SnowflakeIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<line x1="2" x2="22" y1="12" y2="12" />
		<line x1="12" x2="12" y1="2" y2="22" />
		<path d="m20 16-4-4 4-4" />
		<path d="m4 8 4 4-4 4" />
		<path d="m16 4-4 4-4-4" />
		<path d="m8 20 4-4 4 4" />
	</svg>
)

const BoxIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
		<path d="m3.3 7 8.7 5 8.7-5" />
		<path d="M12 22V12" />
	</svg>
)

const ImageIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
		<circle cx="9" cy="9" r="2" />
		<path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
	</svg>
)

const CoinsIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<circle cx="8" cy="8" r="6" />
		<path d="M18.09 10.37A6 6 0 1 1 10.34 18" />
		<path d="M7 6h1v4" />
		<path d="m16.71 13.88.7.71-2.82 2.82" />
	</svg>
)

const _InfoIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="14"
		height="14"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<circle cx="12" cy="12" r="10" />
		<path d="M12 16v-4" />
		<path d="M12 8h.01" />
	</svg>
)

const TagIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<path d="M12 2H2v10l9.29 9.29c.94.94 2.48.94 3.42 0l6.58-6.58c.94-.94.94-2.48 0-3.42L12 2Z" />
		<path d="M7 7h.01" />
	</svg>
)

const PencilIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="18"
		height="18"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<path d="M12 20h9" />
		<path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
	</svg>
)

const GearIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="18"
		height="18"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
		<circle cx="12" cy="12" r="3" />
	</svg>
)

const SortIcon = () => (
	<svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M4.93 5.43a.45.45 0 0 0 0 .64.45.45 0 0 0 .64 0L7.5 4.14l1.93 1.93a.45.45 0 0 0 .64 0 .45.45 0 0 0 0-.64L7.82 3.18a.45.45 0 0 0-.64 0L4.93 5.43Zm5.14 4.14a.45.45 0 0 0 0-.64.45.45 0 0 0-.64 0L7.5 10.86 5.57 8.93a.45.45 0 0 0-.64 0 .45.45 0 0 0 0 .64l2.25 2.25a.45.45 0 0 0 .64 0l2.25-2.25Z"
			fill="currentColor"
			fillRule="evenodd"
			clipRule="evenodd"
		/>
	</svg>
)

const SearchIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="12"
		height="12"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<circle cx="11" cy="11" r="8" />
		<path d="m21 21-4.3-4.3" />
	</svg>
)

const ChevronRightIcon = ({ className }: { className?: string }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="12"
		height="12"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}
	>
		<path d="m9 18 6-6-6-6" />
	</svg>
)

type Tab = 'my-inscriptions' | 'wallet-history' | 'brc20' | 'sns' | 'utxos'

interface WalletDashboardProps {
	openTab: (tab: Tab, ticker?: string) => void
	toggleSignMessage: () => void
	toggleSendCoin: () => void
	toggleRecieveCoin: () => void
	toggleTopupModal: () => void
	toggleManageUtxos: () => void
}

const ITEMS_PER_PAGE = 48
const TRANSFER_INSCRIPTION_SLUGS = ['brc-20', 'tap', 'rune']
const NAMES_SLUG_SET = new Set(NAMES_SLUGS)
const TRANSFER_INSCRIPTION_SLUG_SET = new Set(TRANSFER_INSCRIPTION_SLUGS)

function escapeRegExp(value: string) {
	return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

// Side-by-side dashboard cards: header, value, CTA, then the scrolling list.
// At lg they share one grid so those four tracks stay the same height.
const DASHBOARD_ACTION_CARD_CLASS =
	'flex h-[338px] flex-col border-y border-border bg-chrome-muted px-5 pt-5 dark:bg-transparent md:rounded-lg md:border lg:h-auto lg:min-h-0 lg:grid lg:grid-rows-subgrid lg:row-span-4'

const DASHBOARD_ACTION_PAIR_CLASS =
	'contents lg:col-span-2 lg:grid lg:h-[338px] lg:min-h-0 lg:grid-cols-2 lg:grid-rows-[auto_auto_auto_minmax(0,1fr)] lg:gap-x-4'

function dashboardActionButtonClass(filled: boolean) {
	return `mb-3 flex min-h-11 w-full shrink-0 items-center justify-center rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
		filled
			? 'bg-purple-600 text-white hover:bg-purple-700'
			: 'border border-border bg-background text-foreground hover:bg-accent'
	}`
}

function DashboardActionCard(props: {
	header: React.ReactNode
	value: React.ReactNode
	action: React.ReactNode
	children: React.ReactNode
}) {
	return (
		<div className={DASHBOARD_ACTION_CARD_CLASS}>
			{props.header}
			<div className="flex flex-col lg:h-full lg:justify-end">{props.value}</div>
			{props.action}
			<ScrollArea className="min-h-0 flex-1 lg:h-full">{props.children}</ScrollArea>
		</div>
	)
}

const WalletDashboard: FC<WalletDashboardProps> = (props): ReactElement => {
	const { toggleSendCoin, toggleRecieveCoin, toggleManageUtxos } = props
	const openInscriptionModal = useInscriptionModal()
	const [copied, setCopied] = React.useState(false)
	const [selectedSns] = React.useState<string>(() => readStoredProfile().sns || '')
	const isMounted = useIsHydrated()

	// Visible batch sizes for scroll loading
	const [inscriptionsLimit, setInscriptionsLimit] = React.useState(ITEMS_PER_PAGE)
	const [tokensLimit, setTokensLimit] = React.useState(ITEMS_PER_PAGE)
	const [soldLimit, setSoldLimit] = React.useState(ITEMS_PER_PAGE)
	const [utxosLimit, setUtxosLimit] = React.useState(ITEMS_PER_PAGE)
	const [frozenLimit, setFrozenLimit] = React.useState(ITEMS_PER_PAGE)

	// Sort states
	const [soldSort, setSoldSort] = React.useState<'recent' | 'amount'>('recent')
	// "Most recent" first: what was just bought is what people come to look at.
	const [inscriptionsSort, setInscriptionsSort] = React.useState<
		'recent' | 'most-value' | 'least-value' | 'highest-num' | 'lowest-num'
	>('recent')
	const [inscriptionsTab, setInscriptionsTab] = React.useState<'all' | 'for-sale'>('all')
	const [inscriptionsSearch, setInscriptionsSearch] = React.useState('')
	const [expandedInscriptionGroups, setExpandedInscriptionGroups] = React.useState<Set<string>>(
		new Set()
	)
	const [tokensSort, setTokensSort] = React.useState<'recent' | 'volume' | 'highest' | 'lowest'>(
		'recent'
	)
	const [tokensFilter, setTokensFilter] = React.useState<string>('all')
	const [tokensSearch, setTokensSearch] = React.useState('')
	const [utxosSort, setUtxosSort] = React.useState<'highest' | 'lowest'>('highest')
	const [frozenSort, setFrozenSort] = React.useState<'highest' | 'lowest'>('highest')

	// PFP dropdown state
	const [pfpDropdownOpen, setPfpDropdownOpen] = React.useState(false)

	// Handle PFP change
	const handleSetPfp = async (inscription: any) => {
		try {
			const message = inscription.id
			const address = Wallet.getTaprootAddress()
			const signature = await Wallet.signInscription(message)

			await axios.post(`${API_URL}/wallet/${address}/icon`, {
				inscription: message,
				signature
			})

			Wallet.fetchProfile()
			setPfpDropdownOpen(false)

			toast({
				title: 'Profile picture updated!',
				description: 'Your new profile picture has been set.'
			})
		} catch (_error) {
			toast({
				title: 'Error',
				description: 'Failed to update profile picture. Please try again.'
			})
		}
	}

	// Check if using external wallet
	const walletType = Wallet.type()
	const isExternalWallet = walletType !== 'ordinalswallet'
	const walletName = Wallet.provider?.name || walletType

	const handleSend = () => {
		toggleSendCoin()
	}

	const handleReceive = () => {
		toggleRecieveCoin()
	}

	// Wallet data
	const { data: walletData, isLoading: isLoadingWallet } = useWalletBalance()
	const walletInscriptions = walletData?.inscriptions
	const { exchangeRate } = useExchangeRate()
	const router = useRouter()
	const { data: brc20Data, isLoading: isLoadingBrc20 } = useBrc20Balance()
	// Live fair marks for everything held. A holding is quoted at the fair
	// value the chart closes at, not at the cheapest listing: one stale ask
	// should not reprice a wallet. The floor remains the fallback for a
	// collection the quote hub has no mark for.
	const heldSlugs = React.useMemo(() => {
		const out = new Set<string>()
		for (const c of brc20Data ?? []) {
			if (c?.collection?.slug) out.add(c.collection.slug)
		}
		for (const insc of walletInscriptions ?? []) {
			if (insc?.collection?.slug) out.add(insc.collection.slug)
		}
		return [...out]
	}, [brc20Data, walletInscriptions])
	const marks = useMarks(heldSlugs)
	const { data: tokenPrices } = useTokenPrices()
	const { priceForSlug: alkanesPriceForSlug } = useAlkanesPrices()

	// Fetch UTXOs
	const address = Wallet.balanceAddress()
	const { data: utxoData } = useSwr(`${API_URL}/wallet/${address}/all-utxos`, fetcher)
	const acquisitions = useAcquisitions(address)

	// Fetch sold items
	const taprootAddress = Wallet.getTaprootAddress()
	const { data: soldData, isLoading: isLoadingSold } = useSwr(
		`${API_URL}/wallet/${taprootAddress}/inscriptions/activity?status=Sold`,
		fetcher
	)

	// Fetch collections with volume data for sorting
	const { data: collectionsData } = useSwr(
		`${API_URL}/${COLLECTIONS_VERSION}?limit=500&order=VolumeTotalDesc`,
		fetcher
	)

	// Volume map for sorting tokens
	const volumeMap = React.useMemo(() => {
		const map = new Map<string, number>()
		if (collectionsData?.collections) {
			collectionsData.collections.forEach((c: any) => {
				if (c?.slug && c?.volume_all_time) {
					map.set(c.slug, c.volume_all_time)
				}
			})
		}
		return map
	}, [collectionsData])

	// Balance formatting
	const balanceBTC = useSatoshiPrice(walletData?.confirmed_balance)
	const [balanceUSD] = useSatoshiPriceUSD(walletData?.confirmed_balance)
	const handleUnfreeze = () => {
		Event.emit('command', {
			command: {
				featured: false,
				component: CollectionDetail,
				name: 'Manage Inscriptions',
				args: { slug: 'frozen', tab: 'refund' },
				heading: ''
			}
		})
	}

	const hasFrozenBalance = !isLoadingWallet && Number(walletData?.frozen_balance) > 0
	const hasUtxos = !isLoadingWallet && Number(walletData?.utxo_count) > 0
	const frozenBalanceBTC = useSatoshiPrice(walletData?.frozen_balance)
	const [frozenBalanceUSD] = useSatoshiPriceUSD(walletData?.frozen_balance)

	// Get user's SNS names from inscriptions
	const snsNames = React.useMemo(() => {
		if (!walletInscriptions) return []
		return walletInscriptions.flatMap((insc: any) =>
			NAMES_SLUG_SET.has(insc?.collection?.slug) && insc?.meta?.name ? [insc.meta.name] : []
		)
	}, [walletInscriptions])

	const profileIcon = readStoredProfile().icon
	// The profile this wallet set (Twetch name + picture) leads; the legacy OW
	// icon and SNS name are the fallback.
	const viewer = useViewerProfile()
	const viewerIcon = resolveTwetchImage(viewer.icon)
	const viewerName = viewer.name?.trim() || null

	const walletAddress = Wallet.getTaprootAddress()
	const paymentAddress = Wallet.balanceAddress?.() || null
	const publicKey = Wallet.getPublicKey()
	const gradientId = parseInt(publicKey, 16)
	const notConnected = isMounted && !walletAddress

	// Process UTXOs sorted by value
	const sortedUtxos = React.useMemo(() => {
		const confirmed = utxoData?.utxos || []
		const unconfirmed = utxoData?.mempool || []
		const all = [...confirmed, ...unconfirmed]
		return all.sort((a: any, b: any) => {
			if (utxosSort === 'lowest') {
				return a.value - b.value
			}
			// Default: highest
			return b.value - a.value
		})
	}, [utxoData, utxosSort])

	// Process inscriptions (excluding SNS names and transfer-inscription slugs)
	const sortedInscriptions = React.useMemo(() => {
		if (!walletInscriptions) return []
		let filtered = [...walletInscriptions].filter(
			(insc: any) =>
				!NAMES_SLUG_SET.has(insc?.collection?.slug) &&
				!TRANSFER_INSCRIPTION_SLUG_SET.has(insc?.collection?.slug)
		)
		// Filter by tab
		if (inscriptionsTab === 'for-sale') {
			filtered = filtered.filter((insc: any) => insc?.escrow?.satoshi_price)
		}
		return filtered.sort((a: any, b: any) => {
			const aUsed = isUsedTransfer(a)
			const bUsed = isUsedTransfer(b)

			// Keep used transfers grouped at the end regardless of selected sort option.
			if (aUsed !== bUsed) {
				return aUsed ? 1 : -1
			}

			switch (inscriptionsSort) {
				case 'recent': {
					// Latest purchase on OW first; anything that arrived by mint or
					// transfer follows, newest inscription number first.
					const at = (i: any) => acquiredAtMs(acquisitions?.inscriptions?.[i?.id])
					const diff = at(b) - at(a)
					if (diff !== 0) return diff
					return (b?.num || b?.number || 0) - (a?.num || a?.number || 0)
				}
				case 'least-value':
					return (a?.collection?.floor_price || 0) - (b?.collection?.floor_price || 0)
				case 'highest-num':
					return (b?.num || b?.number || 0) - (a?.num || a?.number || 0)
				case 'lowest-num':
					return (a?.num || a?.number || 0) - (b?.num || b?.number || 0)
				case 'most-value':
				default:
					return (b?.collection?.floor_price || 0) - (a?.collection?.floor_price || 0)
			}
		})
	}, [walletInscriptions, inscriptionsTab, inscriptionsSort, acquisitions])

	// Count of listed inscriptions for the tab
	const listedInscriptionsCount = React.useMemo(() => {
		if (!walletInscriptions) return 0
		return walletInscriptions.filter(
			(insc: any) => !NAMES_SLUGS.includes(insc?.collection?.slug) && insc?.escrow?.satoshi_price
		).length
	}, [walletInscriptions])

	// Group inscriptions by collection slug and apply search.
	// Items without a slug, or whose collection has only 1 item, render as singletons.
	// Multi-item collections become a stacked entry that can expand.
	const inscriptionGroups = React.useMemo(() => {
		const query = inscriptionsSearch.trim().toLowerCase()
		const queryPattern = query ? new RegExp(escapeRegExp(query)) : null
		const matchesText = (value: string) =>
			queryPattern ? queryPattern.test(value.toLowerCase()) : true

		const matchesQuery = (insc: any): boolean => {
			if (!query) return true
			const name = insc?.collection?.name?.toLowerCase?.() || ''
			const slug = insc?.collection?.slug?.toLowerCase?.() || ''
			const num = String(insc?.num ?? insc?.number ?? '')
			const metaName = insc?.meta?.name?.toLowerCase?.() || ''
			const ticker = isFungible(insc?.collection?.slug) ? getFungibleTicker(insc).toLowerCase() : ''
			return (
				matchesText(name) ||
				matchesText(slug) ||
				matchesText(num) ||
				matchesText(metaName) ||
				matchesText(ticker)
			)
		}

		const bySlug = new Map<string, any[]>()
		for (const insc of sortedInscriptions) {
			const slug = insc?.collection?.slug
			if (!slug) continue
			const arr = bySlug.get(slug) || []
			arr.push(insc)
			bySlug.set(slug, arr)
		}

		type GroupEntry = {
			type: 'group'
			key: string
			slug: string
			name: string
			items: any[]
			collection: any
		}
		type SingleEntry = { type: 'single'; key: string; item: any }
		type Entry = GroupEntry | SingleEntry

		const entries: Entry[] = []
		const seenSlug = new Set<string>()
		for (const insc of sortedInscriptions) {
			const slug = insc?.collection?.slug
			if (slug) {
				if (seenSlug.has(slug)) continue
				seenSlug.add(slug)
				const items = bySlug.get(slug)!
				if (items.length === 1) {
					entries.push({ type: 'single', key: items[0]?.id || `${slug}-0`, item: items[0] })
				} else {
					entries.push({
						type: 'group',
						key: slug,
						slug,
						name: insc?.collection?.name || slug,
						items,
						collection: insc?.collection
					})
				}
			} else {
				entries.push({ type: 'single', key: insc?.id || `nokey-${entries.length}`, item: insc })
			}
		}

		if (!query) return entries

		const filtered: Entry[] = []
		for (const entry of entries) {
			if (entry.type === 'single') {
				if (matchesQuery(entry.item)) filtered.push(entry)
				continue
			}
			const nameMatch = matchesText(entry.name) || matchesText(entry.slug)
			if (nameMatch) {
				filtered.push(entry)
				continue
			}
			const childMatches = entry.items.filter(matchesQuery)
			if (childMatches.length === 0) continue
			if (childMatches.length === 1) {
				filtered.push({
					type: 'single',
					key: childMatches[0]?.id || entry.slug,
					item: childMatches[0]
				})
			} else {
				filtered.push({ ...entry, items: childMatches })
			}
		}
		return filtered
	}, [sortedInscriptions, inscriptionsSearch])

	const isSearchingInscriptions = inscriptionsSearch.trim().length > 0

	const toggleInscriptionGroup = React.useCallback((slug: string) => {
		setExpandedInscriptionGroups((prev) => {
			const next = new Set(prev)
			if (next.has(slug)) next.delete(slug)
			else next.add(slug)
			return next
		})
	}, [])

	// Get image inscriptions for PFP selection
	const imageInscriptions = React.useMemo(() => {
		if (!walletInscriptions) return []
		return walletInscriptions
			.filter((insc: any) => insc?.content_type?.includes('image'))
			.slice(0, 20) // Limit to 20 for dropdown
	}, [walletInscriptions])

	// Process frozen balance items — put outputs with reclaimable excess first.
	const sortedFrozenItems = React.useMemo(() => {
		if (!walletInscriptions) return []
		const filtered = [...walletInscriptions].filter(
			(insc: any) => !NAMES_SLUGS.includes(insc?.collection?.slug)
		)
		// Group by UTXO outpoint — show one row per unique UTXO
		const utxoMap = new Map()
		for (const insc of filtered) {
			// @ts-ignore
			const key = insc?.outpoint?.outpoint || insc?.id
			if (!utxoMap.has(key)) {
				utxoMap.set(key, { ...insc, _utxoGroupCount: 1 })
			} else {
				utxoMap.get(key)._utxoGroupCount += 1
			}
		}
		const grouped: any[] = []
		utxoMap.forEach((v) => grouped.push(v))
		return grouped.sort((a: any, b: any) => {
			const aVal = a?.outpoint?.sats || 546
			const bVal = b?.outpoint?.sats || 546
			const aUnfreezable = getUnfreezableSats(aVal)
			const bUnfreezable = getUnfreezableSats(bVal)
			if (Boolean(aUnfreezable) !== Boolean(bUnfreezable)) return bUnfreezable - aUnfreezable
			if (frozenSort === 'lowest') {
				return aVal - bVal
			}
			// Default: highest
			return bVal - aVal
		})
	}, [walletInscriptions, frozenSort])

	// Process sold items (API returns array directly)
	const soldItems = React.useMemo(() => {
		if (!soldData || !Array.isArray(soldData)) return []
		return [...soldData].sort((a: any, b: any) => {
			if (soldSort === 'amount') {
				return (b?.escrow?.satoshi_price || 0) - (a?.escrow?.satoshi_price || 0)
			}
			// Default: recent
			return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
		})
	}, [soldData, soldSort])

	// Calculate total sold amount in USD
	const totalSoldUSD = React.useMemo(() => {
		if (!soldItems?.length) return 0
		const totalSats = soldItems.reduce(
			(acc: number, item: any) => acc + (item?.escrow?.satoshi_price || 0),
			0
		)
		return (totalSats / 1e8) * exchangeRate
	}, [soldItems, exchangeRate])

	// Process tokens sorted by selected criteria
	const sortedTokens = React.useMemo(() => {
		if (!brc20Data) return []
		return brc20Data
			.map((c: any) => {
				let satoshiPrice = 0
				let balanceUSD = '0.00'
				const priceCollection = (tokenPrices || []).find((e: any) => e.slug === c?.collection?.slug)

				// Alkanes are AMM-traded: their value comes from the pool price,
				// never from the marketplace floor, where one stale ask priced
				// DIESEL 10.6x over its real price and most alkanes (CheekyB
				// among them) have no listing at all. No pool price is a real
				// answer — leave the row unpriced rather than invent a floor.
				const alkanePriceUsd = isAlkanes(c?.collection?.slug)
					? alkanesPriceForSlug(c?.collection?.slug)
					: null

				if (isAlkanes(c?.collection?.slug)) {
					const balance = Number(c?.overall_balance)
					if (alkanePriceUsd && Number.isFinite(balance) && balance > 0 && exchangeRate) {
						satoshiPrice = ((alkanePriceUsd * balance) / exchangeRate) * 1e8
						balanceUSD = getSatoshiPriceUSD(satoshiPrice, exchangeRate)
					}
				} else {
					// Per unit: the fair value or the floor, whichever is lower.
					const unitSats = lowerQuote(
						marks[c?.collection?.slug]?.fair_sats,
						Number(priceCollection?.floor_price_per)
					)
					if (unitSats && c?.overall_balance) {
						satoshiPrice = unitSats * c.overall_balance
						balanceUSD = getSatoshiPriceUSD(satoshiPrice, exchangeRate)
					}
					if (unitSats && isRune(priceCollection?.slug ?? c?.collection?.slug)) {
						satoshiPrice = unitSats * c.amount
						balanceUSD = getSatoshiPriceUSD(satoshiPrice, exchangeRate)
					}
				}

				const volumeAllTime = volumeMap.get(c?.collection?.slug) || 0

				// Merge collection data - keep original collection's icon if priceCollection's icon is empty
				const mergedCollection = priceCollection
					? {
							...c.collection,
							...priceCollection,
							icon: priceCollection.icon || c.collection?.icon
					  }
					: c.collection

				// Determine protocol type
				const slug = c?.collection?.slug || ''
				let protocol = c?.type || 'brc-20'
				if (isRune(slug)) {
					protocol = 'runes'
				} else if (isAlkanes(slug)) {
					protocol = 'alkanes'
				} else if (slug.includes('tap-')) {
					protocol = 'tap'
				} else if (c?.type === 'pow-20') {
					protocol = 'pow-20'
				}

				return {
					...c,
					collection: mergedCollection,
					balanceUSD,
					satoshiPrice,
					volumeAllTime,
					protocol
				}
			})
			.filter((token: any) => token.overall_balance > 0 || token.amount > 0)
			.filter((token: any) => {
				if (tokensFilter === 'all') return true
				return token.protocol === tokensFilter
			})
			.sort((a: any, b: any) => {
				if (tokensSort === 'recent') {
					const at = (t: any) => acquiredAtMs(acquisitions?.tokens?.[t?.collection?.slug])
					const diff = at(b) - at(a)
					if (diff !== 0) return diff
					return b.volumeAllTime - a.volumeAllTime
				}
				if (tokensSort === 'highest' || tokensSort === 'lowest') {
					// A token with no price has an UNKNOWN value, not a zero one: it
					// goes last in both directions, ordered by balance.
					const balanceOf = (t: any) => Number(t.overall_balance || t.amount || 0)
					const av = a.satoshiPrice > 0 ? a.satoshiPrice : null
					const bv = b.satoshiPrice > 0 ? b.satoshiPrice : null
					if (av === null || bv === null) {
						if (av !== null) return -1
						if (bv !== null) return 1
						return balanceOf(b) - balanceOf(a)
					}
					if (av === bv) return balanceOf(b) - balanceOf(a)
					return tokensSort === 'highest' ? bv - av : av - bv
				}
				// Default: volume
				return b.volumeAllTime - a.volumeAllTime
			})
	}, [
		brc20Data,
		tokenPrices,
		alkanesPriceForSlug,
		exchangeRate,
		volumeMap,
		tokensSort,
		tokensFilter,
		acquisitions
	])

	const filteredTokens = React.useMemo(() => {
		const query = tokensSearch.trim().toLowerCase()
		if (!query) return sortedTokens
		return sortedTokens.filter((token: any) => {
			const ticker = (token?.ticker || '').toString().toLowerCase()
			const slug = (token?.collection?.slug || '').toString().toLowerCase()
			const name = (token?.collection?.name || '').toString().toLowerCase()
			const protocol = (token?.protocol || '').toString().toLowerCase()
			return (
				ticker.includes(query) ||
				slug.includes(query) ||
				name.includes(query) ||
				protocol.includes(query)
			)
		})
	}, [sortedTokens, tokensSearch])

	const isSearchingTokens = tokensSearch.trim().length > 0

	const handleCopyAddress = () => {
		copy(walletAddress)
		setCopied(true)
		setTimeout(() => setCopied(false), 2000)
	}

	return (
		<TooltipProvider>
			<div className="w-full py-8 md:py-16 px-0 md:px-4 lg:px-0">
				<RecoverySweepPanel />
				<WalletOffers />
				{/* Row 1: Balance Card, Inscriptions, Tokens */}
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-0 md:gap-4 mb-0 md:mb-6">
					{/* Balance Card */}
					<div className="flex flex-col h-[338px] border-y md:border border-border bg-chrome-muted dark:bg-transparent md:rounded-lg pt-5 px-5">
						{/* Profile Section with Settings */}
						<div className="flex items-start justify-between mb-4">
							<div className="flex items-start gap-3">
								{isMounted ? (
									<Popover
										open={pfpDropdownOpen}
										onOpenChange={(next) => {
											// With a profile, the picture is part of it: edit it there.
											if (next && viewer.userId !== null) {
												openProfileEditor()
												return
											}
											setPfpDropdownOpen(next)
										}}
									>
										<PopoverTrigger asChild>
											<button className="relative group">
												{viewerIcon || profileIcon ? (
													<UserIconStatic
														className="size-12 rounded-full ring-2 ring-border group-hover:ring-purple-500 transition-all"
														src={viewerIcon ?? `${CDN_URL}/${profileIcon}`}
														defaultGradientId={gradientId}
														size={48}
													/>
												) : (
													<UserIconStatic
														className="size-12 rounded-full ring-2 ring-border group-hover:ring-purple-500 transition-all"
														defaultGradientId={gradientId}
														size={48}
													/>
												)}
												<div className="absolute inset-0 rounded-full bg-background/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
													<svg
														xmlns="http://www.w3.org/2000/svg"
														width="16"
														height="16"
														viewBox="0 0 24 24"
														fill="none"
														stroke="white"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													>
														<path d="M12 20h9" />
														<path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
													</svg>
												</div>
											</button>
										</PopoverTrigger>
										<PopoverContent className="w-64 p-2 bg-card border-border" align="start">
											<div className="mb-2 px-2">
												<p className="text-xs text-muted-foreground font-medium">
													Choose Profile Picture
												</p>
											</div>
											<ScrollArea className="h-48">
												<div className="grid grid-cols-4 gap-1">
													{imageInscriptions.map((insc: any, i: number) => (
														<button
															key={i}
															onClick={() => handleSetPfp(insc)}
															className="size-12 rounded overflow-hidden ring-2 ring-transparent hover:ring-purple-500 transition-all"
														>
															<InscriptionContent
																className="size-12 object-cover"
																inscription={insc}
																smallCard={true}
															/>
														</button>
													))}
													{imageInscriptions.length === 0 && (
														<div className="col-span-4 py-4 text-center text-muted-foreground text-xs">
															No image inscriptions found
														</div>
													)}
												</div>
											</ScrollArea>
										</PopoverContent>
									</Popover>
								) : (
									<div className="size-12 rounded-full bg-foreground/[0.08] dark:bg-foreground/[0.12] ring-2 ring-border animate-pulse" />
								)}
								<div className="flex flex-col gap-0.5">
									{isMounted ? (
										walletAddress ? (
											<span className="text-sm font-semibold text-foreground">
												{viewerName ??
													(snsNames.length > 0
														? selectedSns || snsNames[0]
														: abbreviateAddress(walletAddress))}
											</span>
										) : (
											<button
												onClick={() => Event.emit('command', { id: 'connect-wallet' })}
												className="text-sm font-semibold text-foreground hover:text-purple-500 transition-colors text-left"
											>
												Connect Wallet
											</button>
										)
									) : (
										<div className="h-7 w-24 bg-foreground/[0.08] dark:bg-foreground/[0.12] rounded animate-pulse" />
									)}
									<div className="flex flex-col gap-1 mt-2">
										{isMounted ? (
											walletAddress ? (
												<>
													<button
														onClick={handleCopyAddress}
														className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors group"
													>
														{paymentAddress && paymentAddress !== walletAddress && (
															<span className="text-zinc-600 text-[10px]">Ordinals:</span>
														)}
														<span className="font-mono">{abbreviateAddress(walletAddress)}</span>
														<span className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
															{copied ? <CheckIcon /> : <CopyIcon />}
														</span>
														{copied && (
															<span className="text-success text-[10px] flex-shrink-0">
																Copied!
															</span>
														)}
													</button>
													{paymentAddress && paymentAddress !== walletAddress && (
														<button
															onClick={() => {
																copy(paymentAddress)
																setCopied(true)
																setTimeout(() => setCopied(false), 2000)
															}}
															className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors group"
														>
															<span className="text-zinc-600 text-[10px]">Payment:</span>
															<span className="font-mono">{abbreviateAddress(paymentAddress)}</span>
															<span className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
																<CopyIcon />
															</span>
														</button>
													)}
												</>
											) : (
												<span className="text-xs text-muted-foreground">No wallet connected</span>
											)
										) : (
											<div className="h-4 w-32 bg-foreground/[0.08] dark:bg-foreground/[0.12] rounded animate-pulse" />
										)}
									</div>
								</div>
							</div>
							{/* Edit profile + Settings - Top Right */}
							<div className="flex items-center gap-1">
								<TwetchProfileHost />
								{isMounted && walletAddress && (
									<button
										type="button"
										onClick={() => openProfileEditor()}
										aria-label="Edit profile"
										title="Edit profile"
										data-testid="wallet-edit-profile"
										className="text-muted-foreground hover:text-foreground transition-colors p-1"
									>
										<PencilIcon />
									</button>
								)}
								<Link href="/settings">
									<button
										aria-label="Settings"
										title="Settings"
										className="text-muted-foreground hover:text-foreground transition-colors p-1"
									>
										<GearIcon />
									</button>
								</Link>
							</div>
						</div>

						{/* Balance Display - Credit Card Style */}
						<div className="flex-1 flex flex-col justify-center">
							<div className="relative rounded-2xl border border-border p-5 overflow-hidden">
								{/* Decorative blurred purple gradients */}
								<div className="absolute top-0 right-0 size-40 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl bg-gradient-to-br from-purple-500/30 via-violet-600/20 to-[#5B21B6]/30" />
								<div className="absolute bottom-0 left-0 size-32 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl bg-gradient-to-tr from-violet-600/25 via-purple-500/20 to-fuchsia-500/25" />

								{/* Card content */}
								<div className="relative">
									{notConnected ? (
										<>
											<div className="flex items-center justify-between mb-3">
												<span className="text-[10px] text-muted-foreground font-medium">
													Get Started
												</span>
												<BitcoinIcon className="size-6 text-amber-500" />
											</div>
											<p className="text-lg font-bold text-foreground tracking-tight leading-tight mb-1">
												Your Bitcoin & Ordinals home.
											</p>
											<p className="text-xs text-muted-foreground leading-relaxed mb-4">
												Inscriptions, Runes, BRC-20, TAP, and Bitcoin — all in one wallet.
											</p>
											<button
												onClick={() => Event.emit('command', { id: 'connect-wallet' })}
												className="w-full px-4 py-2 bg-foreground text-background font-semibold text-sm rounded-lg hover:bg-foreground/90 transition-colors"
											>
												Connect Wallet
											</button>
											<button
												onClick={() => Event.emit('command', { id: 'connect-wallet' })}
												className="w-full mt-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
											>
												or create a new wallet
											</button>
										</>
									) : (
										<>
											<div className="flex items-center justify-between mb-3">
												<span className="text-[10px] text-muted-foreground font-medium">
													Balance
												</span>
												<BitcoinIcon className="size-6 text-amber-500" />
											</div>

											{isLoadingWallet ? (
												<Skeleton className="h-10 w-40" />
											) : (
												<>
													<div className="flex items-baseline gap-1 mb-1">
														<span className="text-3xl font-bold text-foreground tracking-tight">
															{balanceBTC || '0.00000000'}
														</span>
														<span className="text-sm text-muted-foreground font-medium">BTC</span>
													</div>
													<span className="text-sm text-muted-foreground">
														≈ ${balanceUSD || '0.00'} USD
													</span>
												</>
											)}

											{walletData?.unconfirmed_balance > 0 && (
												<div className="flex items-center gap-2 mt-3 px-2 py-1 bg-amber-500/10 border border-amber-500/20 rounded w-fit">
													<div className="size-1.5 bg-amber-500 rounded-full animate-pulse" />
													<span className="text-amber-400 text-[11px] font-medium">
														+{(walletData.unconfirmed_balance / 1e8).toFixed(8)} incoming
													</span>
												</div>
											)}

											{/* Action Buttons inside card */}
											<div className="flex items-center gap-2 mt-4">
												<button
													onClick={handleReceive}
													className="flex-1 px-4 py-2 bg-foreground text-background font-semibold text-sm rounded-lg hover:bg-foreground/90 transition-colors"
												>
													Receive
												</button>
												<button
													onClick={handleSend}
													className="flex-1 px-4 py-2 bg-accent text-foreground font-semibold text-sm rounded-lg hover:bg-accent/80 transition-colors border border-border"
												>
													Send
												</button>
											</div>
										</>
									)}
								</div>
							</div>
						</div>
					</div>

					{/* Inscriptions Card */}
					<div className="flex flex-col h-[338px] border-y md:border border-border bg-chrome-muted dark:bg-transparent md:rounded-lg pt-5 px-5">
						<div className="flex flex-wrap items-center justify-between gap-2 mb-2">
							<div className="text-sm font-medium text-muted-foreground flex items-center gap-2">
								<ImageIcon />
								Inscriptions
							</div>
							<div className="flex items-center gap-2">
								<button
									onClick={() => {
										Event.emit('command', {
											command: {
												featured: false,
												component: ImportListings,
												name: 'Import Listings',
												heading: 'External Listings',
												args: {}
											}
										})
									}}
									className="h-6 shrink-0 whitespace-nowrap px-2 text-xs text-muted-foreground bg-transparent border border-purple-500/30 rounded-md hover:text-foreground hover:border-purple-500/50 transition-colors"
								>
									Import Listings
								</button>
								<Select
									value={inscriptionsSort}
									onValueChange={(
										value: 'most-value' | 'least-value' | 'highest-num' | 'lowest-num'
									) => {
										setInscriptionsLimit(ITEMS_PER_PAGE)
										setInscriptionsSort(value)
									}}
								>
									<SelectTrigger
										hideChevron
										className="w-auto h-6 shrink-0 whitespace-nowrap bg-transparent border-border text-muted-foreground text-xs px-2 gap-1"
									>
										<SelectValue />
										<SortIcon />
									</SelectTrigger>
									<SelectContent className="bg-card border-border">
										<SelectItem value="recent" className="text-foreground text-xs hover:bg-accent">
											Most Recent
										</SelectItem>
										<SelectItem
											value="most-value"
											className="text-foreground text-xs hover:bg-accent"
										>
											Highest Floor
										</SelectItem>
										<SelectItem
											value="least-value"
											className="text-foreground text-xs hover:bg-accent"
										>
											Lowest Floor
										</SelectItem>
										<SelectItem
											value="highest-num"
											className="text-foreground text-xs hover:bg-accent"
										>
											Highest #
										</SelectItem>
										<SelectItem
											value="lowest-num"
											className="text-foreground text-xs hover:bg-accent"
										>
											Lowest #
										</SelectItem>
									</SelectContent>
								</Select>
							</div>
						</div>
						<div className="flex items-center justify-between mb-3">
							{isLoadingWallet ? (
								<Skeleton className="h-10 w-20" />
							) : (
								<p className="text-3xl font-bold text-foreground">
									{sortedInscriptions?.length || 0}
								</p>
							)}
							{listedInscriptionsCount > 0 && (
								<button
									onClick={() => {
										setInscriptionsLimit(ITEMS_PER_PAGE)
										setInscriptionsTab(inscriptionsTab === 'for-sale' ? 'all' : 'for-sale')
									}}
									className={`text-[10px] px-1.5 py-0.5 rounded transition-colors ${
										inscriptionsTab === 'for-sale'
											? 'bg-purple-600 text-white'
											: 'text-muted-foreground hover:text-foreground'
									}`}
								>
									For Sale ({listedInscriptionsCount})
								</button>
							)}
						</div>
						{/* Search */}
						<div className="relative mb-2">
							<span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
								<SearchIcon />
							</span>
							<input
								type="text"
								value={inscriptionsSearch}
								onChange={(e) => {
									setInscriptionsLimit(ITEMS_PER_PAGE)
									setInscriptionsSearch(e.target.value)
								}}
								placeholder="Search collections or inscriptions"
								className="w-full h-7 pl-7 pr-7 text-xs bg-transparent border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-purple-500/50"
							/>
							{inscriptionsSearch && (
								<button
									onClick={() => {
										setInscriptionsLimit(ITEMS_PER_PAGE)
										setInscriptionsSearch('')
									}}
									className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-xs"
									aria-label="Clear search"
								>
									×
								</button>
							)}
						</div>
						{/* Table Header */}
						<div className="flex items-center justify-between text-[10px] text-muted-foreground px-2 pb-2 border-b border-border">
							<span>Item</span>
							<span>Price</span>
						</div>
						<ScrollArea className="flex-1 min-h-0">
							{inscriptionGroups.length === 0 && !isLoadingWallet ? (
								<div className="flex flex-col items-center justify-center h-full py-8 text-center">
									<p className="text-muted-foreground text-sm">
										{isSearchingInscriptions
											? 'No matches'
											: inscriptionsTab === 'for-sale'
											? 'No listed items'
											: 'No inscriptions'}
									</p>
								</div>
							) : (
								<Table>
									<TableBody>
										{inscriptionGroups.slice(0, inscriptionsLimit).flatMap((entry: any) => {
											const renderItemRow = (insc: any, rowKey: string, indented: boolean) => {
												const isListed = !!insc?.escrow?.satoshi_price
												const isToken = isFungible(insc?.collection?.slug)
												const isSpent = isToken && isUsedTransfer(insc)
												const showListedBadge = isListed && !isSpent
												const tokenLabel = isToken ? getFungibleDisplayLabel(insc) : ''
												const tokenProtocol = isToken ? getFungibleProtocol(insc) : ''
												const listingBtc = isListed
													? (insc.escrow.satoshi_price / 1e8).toFixed(8)
													: null
												const listingUsd = isListed
													? formatUSD((insc.escrow.satoshi_price / 1e8) * exchangeRate)
													: null
												// The fair value or the floor, whichever is lower.
												const quoteSats: number | null = lowerQuote(
													marks[insc?.collection?.slug]?.fair_sats,
													Number(insc?.collection?.floor_price)
												)
												const floorBtc = quoteSats
												const floorUsd = quoteSats
												const itemLabel = isToken
													? tokenLabel
													: indented
													? `#${insc?.num || insc?.number || 'Unknown'}`
													: insc?.collection?.name || `#${insc?.num || insc?.number || 'Unknown'}`
												return (
													<TableRow
														key={rowKey}
														className="cursor-pointer border-border hover:bg-accent/50"
														onClick={(e) => {
															e.stopPropagation()
															if (indented && insc?.id) {
																openInscriptionModal({ id: insc.id, inscription: insc })
																return
															}
															const slug = insc?.collection?.slug
															// Alkanes trade on the AMM: send their rows to the /alkanes
															// market page instead of the marketplace's list/send panel.
															const alkanesPath = alkanesPathForSlug(slug)
															if (alkanesPath) {
																router.push(alkanesPath)
																return
															}
															if (slug) {
																Event.emit('command', {
																	command: {
																		featured: false,
																		component: CollectionDetail,
																		name: isToken
																			? tokenLabel
																			: insc?.collection?.name ||
																			  `#${insc?.num || insc?.number || 'Unknown'}`,
																		args: { slug },
																		heading: isToken ? 'Token' : 'Collection'
																	}
																})
															} else if (insc?.id) {
																openInscriptionModal({ id: insc.id, inscription: insc })
															}
														}}
													>
														<TableCell className={`p-2 ${indented ? 'pl-8' : ''}`}>
															<div className="flex items-center gap-2">
																<div className="size-8 rounded overflow-hidden flex-shrink-0">
																	{isToken ? (
																		<CollectionIcon
																			collection={insc.collection}
																			className="size-8 rounded-full"
																			imageClassName="size-8 rounded-full"
																		/>
																	) : (
																		<InscriptionContent
																			className="size-8 object-cover"
																			inscription={insc}
																			smallCard={true}
																		/>
																	)}
																</div>
																<div className="flex flex-col min-w-0">
																	<div className="flex items-center gap-1.5">
																		{!isToken && insc?.id ? (
																			<button
																				type="button"
																				onClick={(e) => {
																					e.stopPropagation()
																					openInscriptionModal({
																						id: insc.id,
																						inscription: insc
																					})
																				}}
																				className="text-sm text-foreground truncate max-w-[110px] text-left hover:underline"
																			>
																				{itemLabel}
																			</button>
																		) : (
																			<span className="text-sm text-muted-foreground truncate max-w-[110px]">
																				{itemLabel}
																			</span>
																		)}
																		{isSpent && <UsedTransferBadge />}
																		{showListedBadge && (
																			<span className="text-[9px] text-white bg-gradient-to-r from-purple-600 to-blue-500 px-1.5 py-0.5 rounded">
																				Listed
																			</span>
																		)}
																	</div>
																	{isToken && (
																		<span className="text-[10px] text-muted-foreground">
																			{tokenProtocol}
																		</span>
																	)}
																</div>
															</div>
														</TableCell>
														<TableCell className="p-2 text-right">
															{isSpent ? (
																<span className="text-[10px] text-muted-foreground">—</span>
															) : isListed ? (
																<div className="flex flex-col items-end">
																	<span className="text-sm text-muted-foreground">
																		{listingBtc}
																	</span>
																	<span className="text-[10px] text-muted-foreground">
																		${listingUsd}
																	</span>
																</div>
															) : floorBtc ? (
																<div className="flex flex-col items-end">
																	<span className="text-sm text-muted-foreground">
																		~
																		<Ticker
																			value={floorBtc}
																			format={(v) => (v / 1e8).toFixed(8)}
																			snapBelow={0.00001}
																		/>
																	</span>
																	<span className="text-[10px] text-muted-foreground">
																		~$
																		<Ticker
																			value={
																				floorUsd !== null ? (floorUsd / 1e8) * exchangeRate : null
																			}
																			format={(v) => formatUSD(v)}
																			tint={false}
																			snapBelow={0.00001}
																		/>
																	</span>
																</div>
															) : (
																<span className="text-[10px] text-muted-foreground">—</span>
															)}
														</TableCell>
													</TableRow>
												)
											}

											if (entry.type === 'single') {
												return [renderItemRow(entry.item, entry.key, false)]
											}

											const isExpanded =
												isSearchingInscriptions || expandedInscriptionGroups.has(entry.slug)
											const listedCount = entry.items.filter(
												(i: any) => !!i?.escrow?.satoshi_price
											).length
											const floor = entry.collection?.floor_price || 0
											// Per item: the fair value or the floor, whichever is lower.
											const groupQuote: number | null = lowerQuote(
												marks[entry.slug]?.fair_sats,
												floor
											)
											const floorBtc = groupQuote
											const floorUsd = groupQuote
											const stackRow = (
												<TableRow
													key={`group-${entry.slug}`}
													className="cursor-pointer border-border hover:bg-accent/50 bg-accent/20"
													onClick={(e) => {
														e.stopPropagation()
														toggleInscriptionGroup(entry.slug)
													}}
												>
													<TableCell className="p-2">
														<div className="flex items-center gap-2">
															<ChevronRightIcon
																className={`text-muted-foreground transition-transform ${
																	isExpanded ? 'rotate-90' : ''
																}`}
															/>
															<div className="relative size-8 flex-shrink-0">
																<div className="absolute inset-0 rounded overflow-hidden border border-border translate-x-[3px] translate-y-[3px] opacity-50">
																	<CollectionIcon
																		collection={entry.collection}
																		className="w-full h-full"
																		imageClassName="w-full h-full object-cover"
																	/>
																</div>
																<div className="absolute inset-0 rounded overflow-hidden border border-border bg-card">
																	{entry.items[0] && !isFungible(entry.slug) ? (
																		<InscriptionContent
																			className="size-8 object-cover"
																			inscription={entry.items[0]}
																			smallCard={true}
																		/>
																	) : (
																		<CollectionIcon
																			collection={entry.collection}
																			className="size-8 rounded"
																			imageClassName="size-8 rounded object-cover"
																		/>
																	)}
																</div>
															</div>
															<div className="flex flex-col min-w-0">
																<div className="flex items-center gap-1.5">
																	<span className="text-sm text-foreground truncate max-w-[110px]">
																		{entry.name}
																	</span>
																	<span className="text-[9px] text-muted-foreground bg-accent px-1.5 py-0.5 rounded">
																		×{entry.items.length}
																	</span>
																	{listedCount > 0 && (
																		<span className="text-[9px] text-white bg-zinc-700 px-1.5 py-0.5 rounded">
																			{listedCount} listed
																		</span>
																	)}
																</div>
															</div>
														</div>
													</TableCell>
													<TableCell className="p-2 text-right">
														{floorBtc ? (
															<div className="flex flex-col items-end">
																<span className="text-sm text-muted-foreground">
																	~
																	<Ticker
																		value={floorBtc}
																		format={(v) => (v / 1e8).toFixed(8)}
																		snapBelow={0.00001}
																	/>
																</span>
																<span className="text-[10px] text-muted-foreground">
																	~$
																	<Ticker
																		value={
																			floorUsd !== null ? (floorUsd / 1e8) * exchangeRate : null
																		}
																		format={(v) => formatUSD(v)}
																		tint={false}
																		snapBelow={0.00001}
																	/>{' '}
																	ea
																</span>
															</div>
														) : (
															<span className="text-[10px] text-muted-foreground">—</span>
														)}
													</TableCell>
												</TableRow>
											)

											if (!isExpanded) return [stackRow]
											const childRows = entry.items.map((insc: any, idx: number) =>
												renderItemRow(insc, `${entry.slug}-${insc?.id || idx}`, true)
											)
											return [stackRow, ...childRows]
										})}
									</TableBody>
								</Table>
							)}
							<ScrollLoadMore
								hasMore={inscriptionGroups.length > inscriptionsLimit}
								itemCount={Math.min(inscriptionGroups.length, inscriptionsLimit)}
								onLoadMore={() => setInscriptionsLimit((limit) => limit + ITEMS_PER_PAGE)}
							/>
						</ScrollArea>
					</div>

					{/* Tokens Card */}
					<div className="flex flex-col h-[338px] border-y md:border border-border bg-chrome-muted dark:bg-transparent md:rounded-lg pt-5 px-5">
						<div className="flex items-center justify-between mb-2">
							<div className="text-sm font-medium text-muted-foreground flex items-center gap-2">
								<CoinsIcon />
								Tokens
							</div>
							<div className="flex items-center gap-2">
								<Select
									value={tokensFilter}
									onValueChange={(value: string) => {
										setTokensLimit(ITEMS_PER_PAGE)
										setTokensFilter(value)
									}}
								>
									<SelectTrigger className="w-auto h-6 shrink-0 whitespace-nowrap bg-transparent border-border text-muted-foreground text-xs px-2 gap-1">
										<SelectValue />
									</SelectTrigger>
									<SelectContent className="bg-card border-border">
										<SelectItem value="all" className="text-foreground text-xs hover:bg-accent">
											All
										</SelectItem>
										<SelectItem value="runes" className="text-foreground text-xs hover:bg-accent">
											Runes
										</SelectItem>
										<SelectItem value="brc-20" className="text-foreground text-xs hover:bg-accent">
											BRC-20
										</SelectItem>
										<SelectItem value="tap" className="text-foreground text-xs hover:bg-accent">
											TAP
										</SelectItem>
										<SelectItem value="alkanes" className="text-foreground text-xs hover:bg-accent">
											Alkanes
										</SelectItem>
										<SelectItem value="pow-20" className="text-foreground text-xs hover:bg-accent">
											POW-20
										</SelectItem>
									</SelectContent>
								</Select>
								<Select
									value={tokensSort}
									onValueChange={(value: 'recent' | 'volume' | 'highest' | 'lowest') => {
										setTokensLimit(ITEMS_PER_PAGE)
										setTokensSort(value)
									}}
								>
									<SelectTrigger
										hideChevron
										className="w-auto h-6 shrink-0 whitespace-nowrap bg-transparent border-border text-muted-foreground text-xs px-2 gap-1"
									>
										<SelectValue />
										<SortIcon />
									</SelectTrigger>
									<SelectContent className="bg-card border-border">
										<SelectItem value="recent" className="text-foreground text-xs hover:bg-accent">
											Most Recent
										</SelectItem>
										<SelectItem value="volume" className="text-foreground text-xs hover:bg-accent">
											Volume
										</SelectItem>
										<SelectItem value="highest" className="text-foreground text-xs hover:bg-accent">
											Highest Price
										</SelectItem>
										<SelectItem value="lowest" className="text-foreground text-xs hover:bg-accent">
											Lowest Price
										</SelectItem>
									</SelectContent>
								</Select>
							</div>
						</div>
						{isLoadingBrc20 ? (
							<Skeleton className="h-10 w-20 mb-3" />
						) : (
							<p className="text-3xl font-bold text-foreground mb-3">{sortedTokens.length || 0}</p>
						)}
						{/* Search */}
						<div className="relative mb-2">
							<span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
								<SearchIcon />
							</span>
							<input
								type="text"
								value={tokensSearch}
								onChange={(e) => {
									setTokensLimit(ITEMS_PER_PAGE)
									setTokensSearch(e.target.value)
								}}
								placeholder="Search tokens"
								className="w-full h-7 pl-7 pr-7 text-xs bg-transparent border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-purple-500/50"
							/>
							{tokensSearch && (
								<button
									onClick={() => {
										setTokensLimit(ITEMS_PER_PAGE)
										setTokensSearch('')
									}}
									className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-xs"
									aria-label="Clear search"
								>
									×
								</button>
							)}
						</div>
						<ScrollArea className="flex-1 min-h-0">
							{filteredTokens.length === 0 && !isLoadingBrc20 ? (
								<div className="flex flex-col items-center justify-center h-full py-8 text-center">
									<p className="text-muted-foreground text-sm">
										{isSearchingTokens ? 'No matches' : 'No tokens'}
									</p>
								</div>
							) : (
								<Table>
									<TableBody>
										{filteredTokens.slice(0, tokensLimit).map((token: any, i: number) => {
											const isListed = !!token?.escrow?.satoshi_price
											return (
												<TableRow
													key={i}
													className="border-border hover:bg-accent/50 cursor-pointer"
													onClick={(e) => {
														e.stopPropagation()
														const slug = token?.collection?.slug
														// Alkanes trade on the AMM: send their rows to the /alkanes
														// market page instead of the marketplace's list/send panel.
														const alkanesPath = alkanesPathForSlug(slug)
														if (alkanesPath) {
															router.push(alkanesPath)
															return
														}
														if (slug) {
															Event.emit('command', {
																command: {
																	featured: false,
																	component: CollectionDetail,
																	name: isAlkanes(slug) ? token.ticker : slug?.split('-')?.[1],
																	args: { slug },
																	heading: 'Collections'
																}
															})
														}
													}}
												>
													<TableCell className="p-2">
														<div className="flex items-center gap-2">
															<CollectionIcon
																collection={token.collection}
																className="size-8 rounded-full"
																imageClassName="size-8 rounded-full"
															/>
															<div className="flex flex-col">
																<div className="flex items-center gap-1.5">
																	<span className="text-sm text-muted-foreground font-medium truncate max-w-[100px]">
																		{token.ticker?.toUpperCase()}
																	</span>
																	{isListed && (
																		<span className="text-[9px] text-white bg-gradient-to-r from-purple-600 to-blue-500 px-1.5 py-0.5 rounded">
																			Listed
																		</span>
																	)}
																</div>
																<span className="text-[10px] text-muted-foreground">
																	{(token.type || 'brc-20').toUpperCase()}
																</span>
															</div>
														</div>
													</TableCell>
													<TableCell className="p-2 text-right">
														<div className="flex flex-col items-end">
															<span className="text-sm text-muted-foreground">
																{formatNumber(token.overall_balance || token.amount)}
															</span>
															<span className="text-[10px] text-muted-foreground">
																~$
																<Ticker
																	value={
																		token.satoshiPrice > 0
																			? (token.satoshiPrice / 1e8) * exchangeRate
																			: null
																	}
																	format={(v) => formatUSD(v)}
																	placeholder={token.balanceUSD}
																	tint={false}
																/>
															</span>
														</div>
													</TableCell>
												</TableRow>
											)
										})}
									</TableBody>
								</Table>
							)}
							<ScrollLoadMore
								hasMore={filteredTokens.length > tokensLimit}
								itemCount={Math.min(filteredTokens.length, tokensLimit)}
								onLoadMore={() => setTokensLimit((limit) => limit + ITEMS_PER_PAGE)}
							/>
						</ScrollArea>
					</div>
				</div>


				{/* Row 2: Sold Items, UTXOs, Frozen Balance */}
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-0 md:gap-4">
					{/* Sold Items Card */}
					<div className="flex flex-col h-[338px] border-y md:border border-border bg-chrome-muted dark:bg-transparent md:rounded-lg pt-5 px-5">
						<div className="flex items-center justify-between mb-2">
							<div className="text-sm font-medium text-muted-foreground flex items-center gap-2">
								<TagIcon />
								Sold Items
							</div>
							<Select
								value={soldSort}
								onValueChange={(value: 'recent' | 'amount') => {
									setSoldLimit(ITEMS_PER_PAGE)
									setSoldSort(value)
								}}
							>
								<SelectTrigger
									hideChevron
									className="w-auto h-6 shrink-0 whitespace-nowrap bg-transparent border-border text-muted-foreground text-xs px-2 gap-1"
								>
									<SelectValue />
									<SortIcon />
								</SelectTrigger>
								<SelectContent className="bg-card border-border">
									<SelectItem value="recent" className="text-foreground text-xs hover:bg-accent">
										Recent
									</SelectItem>
									<SelectItem value="amount" className="text-foreground text-xs hover:bg-accent">
										Amount
									</SelectItem>
								</SelectContent>
							</Select>
						</div>
						{isLoadingSold ? (
							<Skeleton className="h-10 w-20mb-3" />
						) : (
							<div className="flex items-baseline justify-between mb-3">
								<p className="text-3xl font-bold text-foreground">{soldItems?.length || 0}</p>
								{totalSoldUSD > 0 && (
									<p className="text-sm text-muted-foreground">Total: ${formatUSD(totalSoldUSD)}</p>
								)}
							</div>
						)}
						<ScrollArea className="flex-1 min-h-0">
							<Table>
								<TableBody>
									{soldItems.slice(0, soldLimit).map((item: any, i: number) => {
										const btcPrice = (item?.escrow?.satoshi_price || 0) / 1e8
										const usdPrice = formatUSD(btcPrice * exchangeRate)
										const isToken = isFungible(item?.collection?.slug)
										const isSpent = isToken && isUsedTransfer(item)
										const tokenLabel = isToken ? getFungibleDisplayLabel(item) : ''
										const tokenProtocol = isToken ? getFungibleProtocol(item) : ''
										return (
											<TableRow
												key={i}
												className="border-border hover:bg-accent/50 cursor-pointer"
												onClick={(e) => {
													e.stopPropagation()
													const slug = item?.collection?.slug
													// Alkanes trade on the AMM: send their rows to the /alkanes
													// market page instead of the marketplace's list/send panel.
													const alkanesPath = alkanesPathForSlug(slug)
													if (alkanesPath) {
														router.push(alkanesPath)
														return
													}
													if (slug) {
														Event.emit('command', {
															command: {
																featured: false,
																component: CollectionDetail,
																name: isToken
																	? tokenLabel
																	: item?.collection?.name ||
																	  `#${item?.num || item?.number || 'Unknown'}`,
																args: { slug },
																heading: isToken ? 'Token' : 'Collection'
															}
														})
													}
												}}
											>
												<TableCell className="p-2">
													<div className="flex items-center gap-2">
														{isToken ? (
															<CollectionIcon
																collection={item.collection}
																className="size-8 rounded-full"
																imageClassName="size-8 rounded-full"
															/>
														) : item?.id ? (
															<InscriptionContent
																className="size-8 rounded overflow-hidden"
																inscription={item}
																smallCard={true}
															/>
														) : (
															<CollectionIcon
																collection={item.collection}
																className="size-8"
																imageClassName="size-8"
															/>
														)}
														<div className="flex flex-col">
															<span className="text-sm text-muted-foreground truncate max-w-[100px]">
																{isToken
																	? tokenLabel
																	: item?.collection?.name ||
																	  `#${item?.num || item?.number || 'Unknown'}`}
															</span>
															{isToken && (
																<span className="text-[10px] text-muted-foreground">
																	{tokenProtocol}
																</span>
															)}
														</div>
														{isSpent && <UsedTransferBadge />}
													</div>
												</TableCell>
												<TableCell className="p-2 text-right">
													<div className="flex flex-col items-end">
														<span className="text-sm text-success font-medium">
															{btcPrice.toFixed(8)} BTC
														</span>
														<span className="text-[10px] text-muted-foreground">${usdPrice}</span>
													</div>
												</TableCell>
											</TableRow>
										)
									})}
									{soldItems.length === 0 && !isLoadingSold && (
										<TableRow>
											<TableCell className="py-4 text-center text-muted-foreground text-sm">
												No sold items yet
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
							<ScrollLoadMore
								hasMore={soldItems.length > soldLimit}
								itemCount={Math.min(soldItems.length, soldLimit)}
								onLoadMore={() => setSoldLimit((limit) => limit + ITEMS_PER_PAGE)}
							/>
						</ScrollArea>
					</div>

					<div className={DASHBOARD_ACTION_PAIR_CLASS}>
						<DashboardActionCard
							header={
								<div className="flex items-center justify-between mb-1">
									<div className="text-sm font-medium text-muted-foreground flex items-center gap-2">
										<BoxIcon />
										UTXOs
									</div>
									<Select
										value={utxosSort}
										onValueChange={(value: 'highest' | 'lowest') => {
											setUtxosLimit(ITEMS_PER_PAGE)
											setUtxosSort(value)
										}}
									>
										<SelectTrigger
											hideChevron
											className="w-auto h-6 shrink-0 whitespace-nowrap bg-transparent border-border text-muted-foreground text-xs px-2 gap-1"
										>
											<SelectValue />
											<SortIcon />
										</SelectTrigger>
										<SelectContent className="bg-card border-border">
											<SelectItem
												value="highest"
												className="text-foreground text-xs hover:bg-accent"
											>
												Biggest
											</SelectItem>
											<SelectItem
												value="lowest"
												className="text-foreground text-xs hover:bg-accent"
											>
												Smallest
											</SelectItem>
										</SelectContent>
									</Select>
								</div>
							}
							value={
								<>
									<p className="text-[11px] text-muted-foreground mb-2 leading-relaxed">
										UTXOs are like your money bills on Bitcoin. If you have many you can make many
										transactions quickly without waiting for change.
									</p>
									{isLoadingWallet ? (
										<Skeleton className="h-10 w-20mb-2" />
									) : (
										<p className="text-3xl font-bold text-foreground mb-2">
											{walletData?.utxo_count || 0}
										</p>
									)}
								</>
							}
							action={
								<button
									onClick={toggleManageUtxos}
									type="button"
									className={dashboardActionButtonClass(hasUtxos)}
								>
									Manage your UTXOs
								</button>
							}
						>
							{sortedUtxos.length === 0 && !isLoadingWallet ? (
								<div className="flex flex-col items-center justify-center h-full py-8 text-center">
									<p className="text-muted-foreground text-sm">No UTXOs</p>
								</div>
							) : (
								<Table>
									<TableBody>
										{sortedUtxos.slice(0, utxosLimit).map((utxo: any, i: number) => (
											<TableRow
												key={i}
												className="border-border hover:bg-accent/50 cursor-pointer"
												onClick={() =>
													window.open(`https://mempool.space/tx/${utxo.txid}`, '_blank')
												}
											>
												<TableCell className="p-2">
													<span className="text-sm text-muted-foreground">
														{(utxo.value / 1e8).toFixed(8)}
													</span>
												</TableCell>
												<TableCell className="p-2 text-right">
													<span className="text-sm text-muted-foreground">
														${formatUSD((utxo.value / 1e8) * exchangeRate)}
													</span>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							)}
							<ScrollLoadMore
								hasMore={sortedUtxos.length > utxosLimit}
								itemCount={Math.min(sortedUtxos.length, utxosLimit)}
								onLoadMore={() => setUtxosLimit((limit) => limit + ITEMS_PER_PAGE)}
							/>
						</DashboardActionCard>

						<DashboardActionCard
							header={
								<div className="flex items-center justify-between mb-1">
									<div className="text-sm font-medium text-muted-foreground flex items-center gap-2">
										<SnowflakeIcon />
										BTC in inscriptions
									</div>
									<Select
										value={frozenSort}
										onValueChange={(value: 'highest' | 'lowest') => {
											setFrozenLimit(ITEMS_PER_PAGE)
											setFrozenSort(value)
										}}
									>
										<SelectTrigger
											hideChevron
											className="w-auto h-6 shrink-0 whitespace-nowrap bg-transparent border-border text-muted-foreground text-xs px-2 gap-1"
										>
											<SelectValue />
											<SortIcon />
										</SelectTrigger>
										<SelectContent className="bg-card border-border">
											<SelectItem
												value="highest"
												className="text-foreground text-xs hover:bg-accent"
											>
												Biggest
											</SelectItem>
											<SelectItem
												value="lowest"
												className="text-foreground text-xs hover:bg-accent"
											>
												Smallest
											</SelectItem>
										</SelectContent>
									</Select>
								</div>
							}
							value={
								isLoadingWallet ? (
									<Skeleton className="h-10 w-32mb-1" />
								) : (
									<>
										{/* Same figure treatment as the main balance: the amount
										    leads, the unit sits small at its baseline, the dollar
										    line reads underneath, and the group clears the button. */}
										<div className="flex items-baseline gap-1 mb-1">
											<span className="text-3xl font-bold text-foreground tracking-tight tabular-nums">
												{frozenBalanceBTC || '0'}
											</span>
											<span className="text-sm text-muted-foreground font-medium">BTC</span>
										</div>
										<p className="text-sm text-muted-foreground mb-4">
											≈ ${frozenBalanceUSD || '0.00'} held in inscriptions
										</p>
									</>
								)
							}
							action={
								<button
									onClick={handleUnfreeze}
									type="button"
									className={dashboardActionButtonClass(hasFrozenBalance)}
								>
									Check unfreeze amount
								</button>
							}
						>
							{sortedFrozenItems.length === 0 && !isLoadingWallet ? (
								<div className="flex flex-col items-center justify-center h-full py-8 text-center">
									<p className="text-muted-foreground text-sm">
										{hasFrozenBalance
											? 'Open Unfreeze to review inscriptions'
											: 'No frozen balance'}
									</p>
								</div>
							) : (
								<Table>
									<TableBody>
										{sortedFrozenItems.slice(0, frozenLimit).map((insc: any, i: number) => {
											const isToken = isFungible(insc?.collection?.slug)
											const isListed = !!insc?.escrow?.satoshi_price
											const isSpent = isToken && isUsedTransfer(insc)
											const showListedBadge = isListed && !isSpent
											const tokenLabel = isToken ? getFungibleDisplayLabel(insc) : ''
											const tokenProtocol = isToken ? getFungibleProtocol(insc) : ''

											return (
												<TableRow
													key={i}
													className="border-border hover:bg-accent/50 cursor-pointer"
													onClick={(e) => {
														e.stopPropagation()
														Event.emit('command', {
															command: {
																featured: false,
																component: CollectionDetail,
																name: 'Manage Inscriptions',
																args: { slug: 'frozen', tab: 'refund' },
																heading: ''
															}
														})
													}}
												>
													<TableCell className="p-2">
														<div className="flex items-center gap-2">
															<div className="size-8 rounded overflow-hidden flex-shrink-0">
																{isToken ? (
																	<CollectionIcon
																		collection={insc.collection}
																		className="size-8 rounded-full"
																		imageClassName="size-8 rounded-full"
																	/>
																) : (
																	<InscriptionContent
																		className="size-8 object-cover"
																		inscription={insc}
																		smallCard={true}
																	/>
																)}
															</div>
															<div className="flex flex-col min-w-0">
																<div className="flex items-center gap-1.5">
																	<span className="text-sm text-muted-foreground truncate max-w-[100px]">
																		{isToken
																			? tokenLabel
																			: insc?.collection?.name ||
																			  `#${insc?.num || insc?.number || 'Unknown'}`}
																	</span>
																	{insc?._utxoGroupCount > 1 && (
																		<span className="text-[9px] text-warning bg-yellow-500/15 px-1.5 py-0.5 rounded">
																			×{insc._utxoGroupCount}
																		</span>
																	)}
																	{isSpent && <UsedTransferBadge />}
															{showListedBadge && (
																<span className="text-[9px] text-white bg-gradient-to-r from-purple-600 to-blue-500 px-1.5 py-0.5 rounded">
																	Listed
																</span>
															)}
																</div>
																{isToken && (
																	<span className="text-[10px] text-muted-foreground">
																		{tokenProtocol}
																	</span>
																)}
															</div>
														</div>
													</TableCell>
													<TableCell className="p-2 text-right">
														<div className="flex flex-col items-end">
															<span className="text-sm text-muted-foreground">
																{((insc?.outpoint?.sats || 546) / 1e8).toFixed(8)}
															</span>
															<span className="text-[10px] text-muted-foreground">
																${formatUSD(((insc?.outpoint?.sats || 546) / 1e8) * exchangeRate)}
															</span>
														</div>
													</TableCell>
												</TableRow>
											)
										})}
									</TableBody>
								</Table>
							)}
							<ScrollLoadMore
								hasMore={sortedFrozenItems.length > frozenLimit}
								itemCount={Math.min(sortedFrozenItems.length, frozenLimit)}
								onLoadMore={() => setFrozenLimit((limit) => limit + ITEMS_PER_PAGE)}
							/>
						</DashboardActionCard>
					</div>
				</div>
			</div>
		</TooltipProvider>
	)
}

export default WalletDashboard
